/* global globalThis */
/**
 * Gantt Chart Data Processing Utilities
 * Contains functions for data transformation, parsing, and processing
 */

/**
 * Parse numeric value with fallback
 * @param {any} val - Value to parse
 * @param {number} fallback - Fallback value if parsing fails
 * @returns {number}
 */
export const parseNum = (val, fallback) => {
  if (val == null || val === '') return fallback;
  const n = Number(val);
  return isNaN(n) ? fallback : n;
};

const createSecureFallbackId = () => {
  const webCrypto = globalThis?.crypto;
  if (webCrypto?.randomUUID) {
    return webCrypto.randomUUID();
  }

  if (webCrypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    webCrypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  return `fallback-${Date.now()}`;
};

/**
 * Transform raw data to DHTMLX Gantt format
 * @param {Array} rawData - Raw task data from API
 * @param {Map} teamMap - Map of team IDs to names
 * @returns {Array} Transformed tasks array
 */
export const transformDataToGantt = (rawData, teamMap = new Map()) => {
  if (!Array.isArray(rawData)) return [];

  const tasks = [];
  
  rawData.forEach(n => {
    if (!n || typeof n !== 'object') return;

    const id = String(n.id || n._id || createSecureFallbackId());
    const text = String(n.name || n.text || n.title || 'Untitled Task');
    const parent = n.parent_id || n.parentId || n.parent || '0';

    // Skip if essential data is missing
    if (!id || !text) {
      console.warn('Skipping task with missing id or text:', n);
      return;
    }

    // Parse dates
    const start_raw = n.start_date ?? n.startDate;
    const end_raw = n.end_date ?? n.endDate;
    // Normalize times so bars cover full days (start at 00:00:01, end at 23:59:59)
    const start_date = start_raw ? `${String(start_raw).slice(0,10)} 00:00:01` : null;
    const end_date = end_raw ? `${String(end_raw).slice(0,10)} 23:59:59` : null;
    
    // Parse duration and progress
    const duration = parseNum(n.duration ?? n.days ?? 1, 1);
    let progress = parseNum(n.progress ?? 0, 0);
    if (progress <= 1) progress = Math.round(progress * 100) / 100; // keep as 0..1 for dhtmlx

    // Parse assignment
    const assigned = n.subcontractorName
      ? n.subcontractorName
      : (n.assignedTo != null
          ? (teamMap.get(String(n.assignedTo)) || `Team ID: ${n.assignedTo}`)
          : '');

    // Normalize quantity fields from backend (support multiple aliases)
    const qtyRaw = (n.quantity != null ? n.quantity
                  : (n.totalQuantity != null ? n.totalQuantity
                  : (n.qty != null ? n.qty
                  : (n.total_qty != null ? n.total_qty : null))));
    const compQtyRaw = (n.completedQuantity != null ? n.completedQuantity
                      : (n.completed_qty != null ? n.completed_qty
                      : (n.actualQuantity != null ? n.actualQuantity : null)));
    const qtyNum = parseNum(qtyRaw, null);
    const compQtyNum = parseNum(compQtyRaw, 0);

    tasks.push({
      id,
      text,
      parent,
      start_date,
      end_date,
      duration,
      progress,
      assigned,
      quantity: qtyNum,
      completedQuantity: compQtyNum,
      weight: parseNum(n.weight, 0),
      volume: parseNum(n.volume, 0),
      unit: n.unit || '',
      status: n.status || 'pending',
      priority: n.priority || 'medium',
      type: n.type || 'task',
      open: n.open !== false, // default to open
      // Keep original data for reference
      _raw: n
    });
  });

  return tasks;
};

/**
 * Build hierarchical structure from flat data
 * @param {Array} flatData - Flat array of tasks
 * @returns {Array} Hierarchical task structure
 */
export const buildHierarchy = (flatData) => {
  if (!Array.isArray(flatData)) return [];

  const taskMap = new Map();
  const rootTasks = [];

  // First pass: create map of all tasks
  flatData.forEach(task => {
    taskMap.set(task.id, { ...task, children: [] });
  });

  // Second pass: build hierarchy
  flatData.forEach(task => {
    const taskNode = taskMap.get(task.id);
    if (task.parent && task.parent !== '0' && taskMap.has(task.parent)) {
      const parent = taskMap.get(task.parent);
      parent.children.push(taskNode);
    } else {
      rootTasks.push(taskNode);
    }
  });

  return rootTasks;
};

/**
 * Calculate project statistics
 * @param {Array} tasks - Array of tasks
 * @returns {Object} Project statistics
 */
export const calculateProjectStats = (tasks) => {
  if (!Array.isArray(tasks) || tasks.length === 0) {
    return {
      totalTasks: 0,
      completedTasks: 0,
      inProgressTasks: 0,
      pendingTasks: 0,
      overallProgress: 0,
      totalDuration: 0,
      criticalPath: []
    };
  }

  const stats = {
    totalTasks: tasks.length,
    completedTasks: 0,
    inProgressTasks: 0,
    pendingTasks: 0,
    overallProgress: 0,
    totalDuration: 0,
    criticalPath: []
  };

  let totalProgress = 0;
  let totalDuration = 0;

  tasks.forEach(task => {
    // Count task statuses
    if (task.progress >= 1 || task.status === 'completed') {
      stats.completedTasks++;
    } else if (task.progress > 0 || task.status === 'in_progress') {
      stats.inProgressTasks++;
    } else {
      stats.pendingTasks++;
    }

    // Sum progress and duration
    totalProgress += parseNum(task.progress, 0);
    totalDuration += parseNum(task.duration, 0);
  });

  stats.overallProgress = Math.round((totalProgress / tasks.length) * 100) / 100;
  stats.totalDuration = totalDuration;

  return stats;
};

/**
 * Filter tasks based on criteria
 * @param {Array} tasks - Array of tasks
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered tasks
 */
export const filterTasks = (tasks, filters = {}) => {
  if (!Array.isArray(tasks)) return [];
  if (!filters || Object.keys(filters).length === 0) return tasks;

  return tasks.filter(task => {
    // Status filter
    if (filters.status && filters.status !== 'all') {
      if (filters.status === 'completed' && task.progress < 1) return false;
      if (filters.status === 'in_progress' && (task.progress === 0 || task.progress >= 1)) return false;
      if (filters.status === 'pending' && task.progress > 0) return false;
    }

    // Priority filter
    if (filters.priority && filters.priority !== 'all') {
      if (task.priority !== filters.priority) return false;
    }

    // Assigned filter
    if (filters.assigned && filters.assigned !== 'all') {
      if (!task.assigned || !task.assigned.includes(filters.assigned)) return false;
    }

    // Date range filter
    if (filters.startDate || filters.endDate) {
      const taskStart = new Date(task.start_date);
      const taskEnd = new Date(task.end_date);
      
      if (filters.startDate && taskStart < new Date(filters.startDate)) return false;
      if (filters.endDate && taskEnd > new Date(filters.endDate)) return false;
    }

    // Text search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const taskText = task.text.toLowerCase();
      if (!taskText.includes(searchTerm)) return false;
    }

    return true;
  });
};

/**
 * Sort tasks by specified criteria
 * @param {Array} tasks - Array of tasks
 * @param {string} sortBy - Sort criteria (start_date, end_date, progress, text, etc.)
 * @param {string} sortOrder - Sort order (asc, desc)
 * @returns {Array} Sorted tasks
 */
export const sortTasks = (tasks, sortBy = 'start_date', sortOrder = 'asc') => {
  if (!Array.isArray(tasks)) return [];

  return [...tasks].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];

    // Handle different data types
    if (sortBy === 'start_date' || sortBy === 'end_date') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    } else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    // Compare values
    let comparison = 0;
    if (aVal < bVal) comparison = -1;
    else if (aVal > bVal) comparison = 1;

    return sortOrder === 'desc' ? -comparison : comparison;
  });
};

/**
 * Validate task data
 * @param {Object} task - Task object to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateTask = (task) => {
  const errors = [];

  if (!task.id) errors.push('Task ID is required');
  if (!task.text || task.text.trim() === '') errors.push('Task name is required');
  if (!task.start_date) errors.push('Start date is required');
  if (!task.end_date) errors.push('End date is required');
  
  if (task.start_date && task.end_date) {
    const startDate = new Date(task.start_date);
    const endDate = new Date(task.end_date);
    if (startDate > endDate) errors.push('Start date must be before end date');
  }

  if (task.progress < 0 || task.progress > 1) {
    errors.push('Progress must be between 0 and 1');
  }

  if (task.duration && task.duration <= 0) {
    errors.push('Duration must be greater than 0');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Generate unique task ID
 * @param {Array} existingTasks - Array of existing tasks
 * @returns {string} Unique task ID
 */
export const generateTaskId = (existingTasks = []) => {
  const existingIds = new Set(existingTasks.map(t => t.id));
  let id;
  let counter = 1;
  
  do {
    id = `task_${Date.now()}_${counter}`;
    counter++;
  } while (existingIds.has(id));
  
  return id;
};
